import { defineStore } from "pinia";
import { computed, ref } from "vue";

/**
 * Cola de notificaciones, renderizada por AgSnackbarHost en App.vue.
 *
 * Reemplaza a vue-notification, que además estaba declarada en devDependencies
 * pese a que main.ts la importaba en runtime — una construcción con
 * `npm ci --omit=dev` habría fallado.
 */

export type NotificationType = "success" | "info" | "warning" | "error";

export interface Notification {
  id: number;
  type: NotificationType;
  title?: string;
  text: string;
  /** ms. 0 la deja fija hasta que se cierre a mano. */
  timeout: number;
}

/** Los errores se quedan más tiempo: hay algo que leer. */
const DEFAULT_TIMEOUT: Record<NotificationType, number> = {
  success: 4000,
  info: 5000,
  warning: 7000,
  error: 9000,
};

let nextId = 1;

export const useNotificationsStore = defineStore("notifications", () => {
  const queue = ref<Notification[]>([]);

  /** La que se está mostrando, si hay alguna. */
  const current = computed<Notification | null>(() => queue.value[0] ?? null);

  function push(n: Omit<Notification, "id" | "timeout"> & { timeout?: number }) {
    queue.value.push({
      id: nextId++,
      timeout: n.timeout ?? DEFAULT_TIMEOUT[n.type],
      ...n,
    });
  }

  function dismiss(id: number) {
    queue.value = queue.value.filter((n) => n.id !== id);
  }

  function clear() {
    queue.value = [];
  }

  const success = (text: string, title?: string) => push({ type: "success", text, title });
  const info = (text: string, title?: string) => push({ type: "info", text, title });
  const warning = (text: string, title?: string) => push({ type: "warning", text, title });
  const error = (text: string, title?: string) => push({ type: "error", text, title });

  return { queue, current, push, dismiss, clear, success, info, warning, error };
});
